import { Injectable, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { take, catchError, of } from 'rxjs';
import { PusherService } from './pusher.service';
import { JobsStatusService, JobStatus } from '@modules/settings/jobs-status/jobs-status.service';
import { JobsStatusActions } from '@modules/settings/jobs-status/state/actions';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { TsSnackbarService } from 'tsui';
import { WINDOW } from 'app/providers/window.provider';
import { DIRECT_DOWNLOAD_JOBS } from './direct-download-jobs';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';

interface PusherJobStatusPayload {
  account: number;
  error?: string;
  data: {
    job_status: {
      id: number;
      job_purpose: string;
      status: string;
      normalized_state: 'in_progress' | 'completed' | 'failed';
      progress_current: number | null;
      progress_total: number | null;
      progress_percent: number | null;
      progress_stage: string | null;
    };
  };
  user_id: number;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalJobStatusService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly pusherService = inject(PusherService);
  private readonly jobsStatusService = inject(JobsStatusService);
  private readonly snackbarService = inject(TsSnackbarService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WINDOW);

  private accountId: number | null = null;
  private userId: number | null = null;

  initializeListeners(): void {
    const account = this.store.selectSnapshot(AccountState.getAccount);
    const user = this.store.selectSnapshot(UserState.getUser);

    if (account?.id && user?.id) {
      this.accountId = +account.id;
      this.userId = user.id;

      this.pusherService
        .listenToChannel<PusherJobStatusPayload>({
          channelName: `job-status-push-ts-user-${this.userId}`,
          eventName: 'job_status_update',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((payload) => {
          console.log('User job status payload', payload);
          this.handleJobStatusUpdate(payload as PusherJobStatusPayload);
        });
    } else {
      console.log('No account or user found for job status listener');
    }
  }

  cleanup(): void {
    if (this.userId) {
      this.pusherService.closeChannel(`job-status-push-ts-user-${this.userId}`);
    }
  }

  private handleJobStatusUpdate(payload: PusherJobStatusPayload): void {
    if (!payload.data?.job_status) {
      return;
    }

    const jobStatusUpdate = payload.data.job_status;
    const jobId = jobStatusUpdate.id;

    const currentJobs = this.store.selectSnapshot<JobStatus[]>((state) => state.jobsStatusState?.jobs || []);
    const existingJob = currentJobs.find((job) => job.id === jobId);

    const directDownloadJobChanged =
      !!DIRECT_DOWNLOAD_JOBS[existingJob?.job_class] && existingJob.status !== jobStatusUpdate.status;

    if (jobStatusUpdate.normalized_state !== 'in_progress' || directDownloadJobChanged) {
      this.notificationsService.triggerRefresh();
    }

    const isCompleted = jobStatusUpdate.normalized_state === 'completed';
    // The Pusher payload has no job_class or document URL, so direct download toasts wait for the full job.
    const awaitsFullJob = isCompleted && (!existingJob || !!DIRECT_DOWNLOAD_JOBS[existingJob.job_class]);

    if (!awaitsFullJob) {
      this.notifyJobStatus(jobStatusUpdate.job_purpose, jobStatusUpdate.normalized_state, payload.error);
    }

    if (!existingJob || isCompleted) {
      this.jobsStatusService
        .getJobStatusById(jobId)
        .pipe(
          take(1),
          catchError((error) => {
            console.error('Error fetching job status:', error);
            return of(jobStatusUpdate);
          }),
        )
        .subscribe((completeJob) => {
          if (awaitsFullJob) {
            this.notifyJobStatus(
              jobStatusUpdate.job_purpose,
              jobStatusUpdate.normalized_state,
              payload.error,
              completeJob as Partial<JobStatus>,
            );
          }

          if (!completeJob) {
            return;
          }

          if (existingJob) {
            this.store.dispatch(new JobsStatusActions.JobUpdated(jobId, completeJob as Partial<JobStatus>));
          } else {
            this.store.dispatch(new JobsStatusActions.JobAdded(completeJob as JobStatus));
          }

          if (!existingJob && !isCompleted && DIRECT_DOWNLOAD_JOBS[(completeJob as Partial<JobStatus>).job_class]) {
            this.notificationsService.triggerRefresh();
          }
        });
    } else {
      const updatedJob: Partial<JobStatus> = {
        id: jobId,
        status: jobStatusUpdate.status as JobStatus['status'],
        ...(jobStatusUpdate.job_purpose && { object_name: jobStatusUpdate.job_purpose }),
      };

      this.store.dispatch(new JobsStatusActions.JobUpdated(jobId, updatedJob));
    }
  }

  private notifyJobStatus(purpose: string, status: string, error?: string, job?: Partial<JobStatus>): void {
    const snackbarOpts = { duration: 5000, horizontalPosition: 'right' as const, verticalPosition: 'top' as const };
    const navigateToJobs = () => this.router.navigate(['/settings/jobs-status']);
    const hasDirectDownload = !!DIRECT_DOWNLOAD_JOBS[job?.job_class] && job.latest_document_url;

    if (status === 'completed' && hasDirectDownload) {
      const directDownloadMessage = DIRECT_DOWNLOAD_JOBS[job?.job_class];
      this.snackbarService
        .open(directDownloadMessage, 'success', 'DOWNLOAD', {
          ...snackbarOpts,
          panelClass: ['ts-snackbar__export-success'],
        })
        .onAction()
        .subscribe(() => this.window.open(job.latest_document_url, '_blank'));
      return;
    }

    switch (`${purpose}:${status}`) {
      case 'object_export:completed': {
        const ref = this.snackbarService.open('Export completed successfully', 'success', 'VIEW', {
          ...snackbarOpts,
          panelClass: ['ts-snackbar__export-success'],
        });
        ref.onAction().subscribe(navigateToJobs);
        break;
      }
      case 'object_export:failed': {
        const msg = error ? `Export failed: ${error}` : 'Export failed';
        this.snackbarService.open(msg, 'error', 'VIEW', snackbarOpts).onAction().subscribe(navigateToJobs);
        break;
      }
      case 'portfolio_creation:completed':
        this.snackbarService.open('Portfolio created successfully', 'success', undefined, snackbarOpts);
        break;
      case 'portfolio_creation:failed':
        this.snackbarService.open('Portfolio creation failed', 'error', undefined, snackbarOpts);
        break;
    }
  }
}
